import {
	PanelBody,
	SelectControl,
	ToggleControl,
	TextControl,
	RangeControl,
} from '@wordpress/components';
import { __ } from '@wordpress/i18n';

/**
 * Legend, direct labels, number format and the table view.
 */
export default function LabelsControls( {
	attributes,
	setAttributes,
	config,
} ) {
	// A legend for a single series only restates whatever heading sits above
	// the chart, so the control says so rather than appearing to do nothing.
	const legendMatters = config.isRadial
		? config.categories.length > 1
		: config.series.length > 1;

	return (
		<PanelBody
			title={ __( 'Labels & legend', 'brandy-blocks' ) }
			initialOpen={ false }
		>
			<ToggleControl
				__nextHasNoMarginBottom
				label={ __( 'Legend', 'brandy-blocks' ) }
				help={
					legendMatters
						? undefined
						: __(
								'Hidden while there is one series — the heading above the chart already names it.',
								'brandy-blocks'
						  )
				}
				checked={ !! attributes.showLegend }
				onChange={ ( value ) => setAttributes( { showLegend: value } ) }
			/>

			{ attributes.showLegend && legendMatters && (
				<SelectControl
					__nextHasNoMarginBottom
					__next40pxDefaultSize
					label={ __( 'Legend position', 'brandy-blocks' ) }
					value={ attributes.legendPosition }
					options={ [
						{ label: __( 'Below', 'brandy-blocks' ), value: 'bottom' },
						{ label: __( 'Above', 'brandy-blocks' ), value: 'top' },
					] }
					onChange={ ( value ) =>
						setAttributes( { legendPosition: value } )
					}
				/>
			) }

			<SelectControl
				__nextHasNoMarginBottom
				__next40pxDefaultSize
				label={ __( 'Value labels', 'brandy-blocks' ) }
				value={ attributes.valueLabels }
				help={ __(
					'Direct labels work because they are sparing. “Highlights” marks the point the chart is read for; a number beside every mark goes unread.',
					'brandy-blocks'
				) }
				options={ [
					{ label: __( 'None', 'brandy-blocks' ), value: 'none' },
					{
						label: __( 'Highlights', 'brandy-blocks' ),
						value: 'selective',
					},
					{ label: __( 'Every value', 'brandy-blocks' ), value: 'all' },
				] }
				onChange={ ( value ) =>
					setAttributes( { valueLabels: value } )
				}
			/>

			<ToggleControl
				__nextHasNoMarginBottom
				label={ __( 'Tooltips on hover', 'brandy-blocks' ) }
				checked={ !! attributes.showTooltip }
				onChange={ ( value ) => setAttributes( { showTooltip: value } ) }
			/>

			<ToggleControl
				__nextHasNoMarginBottom
				label={ __( 'Data table', 'brandy-blocks' ) }
				help={ __(
					'A collapsed table under the chart. It is how a reader who cannot hover — or cannot separate two of the colours — still gets every value. Leave it on.',
					'brandy-blocks'
				) }
				checked={ !! attributes.showDataTable }
				onChange={ ( value ) =>
					setAttributes( { showDataTable: value } )
				}
			/>

			{ attributes.showDataTable && (
				<>
					<TextControl
						__nextHasNoMarginBottom
						label={ __( 'Table toggle text', 'brandy-blocks' ) }
						value={ attributes.dataTableLabel }
						onChange={ ( value ) =>
							setAttributes( { dataTableLabel: value } )
						}
					/>
					<TextControl
						__nextHasNoMarginBottom
						label={ __( 'Category column heading', 'brandy-blocks' ) }
						value={ attributes.categoryHeading }
						onChange={ ( value ) =>
							setAttributes( { categoryHeading: value } )
						}
					/>
				</>
			) }

			<TextControl
				__nextHasNoMarginBottom
				label={ __( 'Value prefix', 'brandy-blocks' ) }
				value={ attributes.valuePrefix }
				onChange={ ( value ) =>
					setAttributes( { valuePrefix: value } )
				}
			/>
			<TextControl
				__nextHasNoMarginBottom
				label={ __( 'Value suffix', 'brandy-blocks' ) }
				value={ attributes.valueSuffix }
				onChange={ ( value ) =>
					setAttributes( { valueSuffix: value } )
				}
			/>
			<RangeControl
				__nextHasNoMarginBottom
				label={ __( 'Decimal places', 'brandy-blocks' ) }
				value={ attributes.decimals }
				min={ 0 }
				max={ 4 }
				onChange={ ( value ) =>
					setAttributes( { decimals: value ?? 0 } )
				}
			/>
			<ToggleControl
				__nextHasNoMarginBottom
				label={ __( 'Thousand separator', 'brandy-blocks' ) }
				checked={ !! attributes.thousandSeparator }
				onChange={ ( value ) =>
					setAttributes( { thousandSeparator: value } )
				}
			/>

			<TextControl
				__nextHasNoMarginBottom
				label={ __( 'Description for screen readers', 'brandy-blocks' ) }
				help={ __(
					'What the chart shows, in one sentence. Leave empty to build it from the series names.',
					'brandy-blocks'
				) }
				value={ attributes.chartLabel }
				onChange={ ( value ) => setAttributes( { chartLabel: value } ) }
			/>
		</PanelBody>
	);
}
